import { z } from "zod";

/**
 * Catálogo de plugins. Cada plugin declara o que pode fazer (permissões) e o front-end
 * só concede essas capacidades a ele — o mesmo modelo das lojas de extensões.
 * A instalação é por pessoa e fica no servidor (vale em qualquer navegador).
 */
export const PLUGIN_CATEGORIES = [
  "ia",
  "linguagens",
  "devops",
  "seguranca",
  "editor",
  "aparencia",
  "produtividade",
  "colaboracao",
] as const;
export type PluginCategory = (typeof PLUGIN_CATEGORIES)[number];

export const PLUGIN_CATEGORY_LABELS: Record<PluginCategory, string> = {
  ia: "IA",
  linguagens: "Linguagens",
  devops: "DevOps",
  seguranca: "Segurança",
  editor: "Editor",
  aparencia: "Aparência",
  produtividade: "Produtividade",
  colaboracao: "Colaboração",
};

export const PLUGIN_PERMISSIONS = {
  "editor.decorate": "Destacar trechos do código (só visual)",
  "editor.edit": "Alterar o arquivo aberto quando você pedir",
  "editor.keymap": "Mudar atalhos de teclado do editor",
  "editor.theme": "Mudar as cores do código no editor",
  "ui.theme": "Mudar as cores da interface da IDE (painéis, menus, terminal)",
  "ui.layout": "Esconder e mostrar painéis da interface",
  "ui.statusbar": "Mostrar informações na barra de status",
  "ui.icons": "Trocar os ícones da árvore de arquivos",
  "files.write": "Criar e alterar arquivos do projeto quando você aprovar",
  "terminal.run": "Rodar comandos no SEU terminal quando você pedir",
  "ai.project": "Enviar trechos do projeto à IA (só se o dono ligou a IA no projeto)",
  "history.read": "Ler o histórico de alterações do projeto",
  commands: "Adicionar comandos à paleta (Ctrl+P)",
  "preview.local": "Servir os arquivos do projeto em localhost (só na sua máquina)",
} as const;
export type PluginPermission = keyof typeof PLUGIN_PERMISSIONS;

export interface PluginManifest {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: PluginCategory;
  version: string;
  author: string;
  /** Nome do ícone (o front mapeia para o componente). */
  icon: string;
  permissions: PluginPermission[];
  tags: string[];
  featured?: boolean;
  /**
   * Imagens ou vídeos de demonstração (caminhos em apps/web/public, ex.: "/plugins/vim.webm").
   * Sem mídia, a loja mostra a demonstração ao vivo (o próprio editor com o plugin ligado).
   */
  media?: ReadonlyArray<{ type: "image" | "video"; src: string; caption?: string }>;
}

export const PLUGIN_CATALOG = [
  {
    id: "kompil.claude-agent",
    name: "Claude",
    tagline: "O Claude, da Anthropic, no canto da IDE: chat e agente de código",
    description:
      "Instale e o ícone do Claude aparece no canto superior direito: clique para abrir o chat com o contexto do projeto (arquivo aberto, seleção e os arquivos mais relevantes). Para tarefas maiores, o Agente Claude (Ctrl+Shift+A) altera vários arquivos e você revisa o diff de cada um antes de aplicar — como uma edição normal, que todo o time acompanha. Usa o modelo Claude da Anthropic pelo serviço de IA do Kompil e respeita o consentimento de IA do projeto. Quem usa o Claude Code no VS Code recebe este plugin ao importar do VS Code.",
    category: "ia",
    version: "2.0.0",
    author: "Anthropic · Kompil",
    icon: "claude",
    permissions: ["ai.project", "files.write", "commands", "ui.statusbar", "ui.layout"],
    tags: ["ia", "claude", "anthropic", "chat", "agente", "multi-arquivo"],
    featured: true,
  },
  {
    id: "kompil.copilot",
    name: "Ações rápidas de IA",
    tagline: "Explicar, testar, documentar e corrigir com um atalho",
    description:
      "Comandos de IA direto no editor: explicar a seleção, gerar testes para o arquivo, documentar uma função, procurar bugs e sugerir uma refatoração. Ctrl+I pergunta sobre o trecho selecionado. As respostas abrem no chat do Claude (precisa do plugin Claude).",
    category: "ia",
    version: "1.1.0",
    author: "Kompil",
    icon: "wand",
    permissions: ["ai.project", "commands", "editor.keymap"],
    tags: ["ia", "testes", "documentação", "explicar", "refatorar"],
  },
  {
    id: "kompil.github-copilot",
    name: "GitHub Copilot",
    tagline: "O Copilot oficial: sugestões enquanto você digita e o Copilot Chat no canto",
    description:
      "Liga o GitHub Copilot oficial ao Kompil Desktop. Sugestões como texto fantasma (Tab aceita, Esc descarta) e o Copilot Chat: o ícone aparece no canto superior direito e abre o chat/agente do Copilot, com a escolha do modelo do seu plano. O agente lê o projeto e, quando quer editar arquivos ou rodar um comando no seu computador, pede permissão antes. Tudo pelo servidor oficial do Copilot (o mesmo de outros editores, no modo ACP). Conecte sua conta do GitHub pelo código de dispositivo — o plano gratuito serve. O projeto só vai para o GitHub se a IA estiver ligada nele.",
    category: "ia",
    version: "2.0.0",
    author: "GitHub · Kompil",
    icon: "copilot",
    permissions: ["ai.project", "ui.statusbar", "commands", "files.write", "terminal.run", "ui.layout"],
    tags: ["copilot", "github", "ia", "chat", "agente", "autocomplete"],
    featured: true,
  },
  {
    id: "kompil.live-server",
    name: "Live Server",
    tagline: "Seu site em localhost, recarregando a cada alteração",
    description:
      "Abre o site do projeto (HTML, CSS e JavaScript) em http://localhost:5500 no seu navegador e recarrega a página a cada alteração — sua ou de quem está programando junto. Serve a pasta onde está o index.html (a raiz ou public/). Vários projetos podem ficar no ar ao mesmo tempo (5500, 5501…), mesmo depois de sair do projeto. Clique em “Go Live” na barra de status. Roda só na sua máquina, no Kompil Desktop.",
    category: "produtividade",
    version: "1.0.0",
    author: "Kompil",
    icon: "radio",
    permissions: ["preview.local", "ui.statusbar", "commands"],
    tags: ["live server", "localhost", "html", "css", "preview", "go live"],
    featured: true,
  },
  {
    id: "kompil.docker",
    name: "Docker",
    tagline: "Dockerfile e Compose com boas práticas e geração automática",
    description:
      "Realce de Dockerfile, avisos de boas práticas enquanto você escreve (imagem sem versão, rodar como root, ADD no lugar de COPY, cache do apt sem limpeza, segredos em ENV) e comandos para gerar Dockerfile, .dockerignore e docker-compose.yml para Node, Python ou Java. No Kompil Desktop, roda o projeto num contêiner na sua máquina.",
    category: "devops",
    version: "1.0.0",
    author: "Kompil",
    icon: "container",
    permissions: ["editor.decorate", "files.write", "commands"],
    tags: ["docker", "compose", "containers", "devops"],
    featured: true,
  },
  {
    id: "kompil.java",
    name: "Java",
    tagline: "Snippets, executar a classe aberta e checagens do Java",
    description:
      "Snippets (main, sysout, for, try/catch, classe, record, interface, teste JUnit, controller Spring Boot), aviso quando a classe pública não tem o nome do arquivo e comando para compilar e rodar a classe aberta no seu terminal (Java 17 na nuvem).",
    category: "linguagens",
    version: "1.0.0",
    author: "Kompil",
    icon: "coffee",
    permissions: ["editor.decorate", "editor.edit", "terminal.run", "commands"],
    tags: ["java", "spring", "junit", "jdk"],
    featured: true,
  },
  {
    id: "kompil.python",
    name: "Python",
    tagline: "Snippets, rodar o arquivo, pytest e avisos de estilo (PEP 8)",
    description:
      "Snippets (def, class, dataclass, main, with, try, rota FastAPI, teste pytest), avisos de estilo PEP 8 (tabs, linhas longas, comparação com None, except sem tipo) e comandos para rodar o arquivo aberto, rodar os testes com pytest e criar um ambiente virtual.",
    category: "linguagens",
    version: "1.0.0",
    author: "Kompil",
    icon: "python",
    permissions: ["editor.decorate", "editor.edit", "terminal.run", "commands"],
    tags: ["python", "pytest", "fastapi", "pep8"],
  },
  {
    id: "kompil.secret-scanner",
    name: "Detector de segredos",
    tagline: "Avisa quando chaves, tokens e senhas aparecem no código",
    description:
      "AppSec no editor: sublinha chaves de nuvem (AWS, Google), tokens (GitHub, GitLab, Slack, Stripe, OpenAI, Anthropic, JWT), chaves privadas e senhas escritas no código, e explica como mover para variáveis de ambiente. Roda só no seu navegador: nada é enviado a lugar nenhum.",
    category: "seguranca",
    version: "1.0.0",
    author: "Kompil",
    icon: "shield",
    permissions: ["editor.decorate"],
    tags: ["segurança", "appsec", "segredos", "tokens"],
    featured: true,
  },
  {
    id: "kompil.authorship",
    name: "Autoria do arquivo",
    tagline: "Quem mudou o arquivo, quando e quanto, no estilo GitLens",
    description:
      "Mostra na barra de status a última alteração do arquivo aberto (pessoa e quando), a partir do histórico sem commit, e adiciona um comando para ver todo o histórico do arquivo.",
    category: "colaboracao",
    version: "1.0.0",
    author: "Kompil",
    icon: "history",
    permissions: ["history.read", "ui.statusbar", "commands"],
    tags: ["histórico", "autoria", "blame", "gitlens"],
  },
  {
    id: "kompil.file-icons",
    name: "Ícones de pasta",
    tagline: "Pastas coloridas por tipo, como nos pacotes de ícones do VS Code",
    description:
      "Os arquivos já mostram o ícone da linguagem (TypeScript, React, HTML, CSS, Python, Java, Docker…) em toda a IDE. Este plugin completa o visual dos pacotes de ícones do VS Code (Material Icon Theme, vscode-icons): as pastas comuns (src, public, components, test, assets, docs…) ganham ícone e cor próprios na árvore de arquivos.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "folder-tree",
    permissions: ["ui.icons"],
    tags: ["ícones", "árvore", "aparência"],
  },
  {
    id: "kompil.snippets",
    name: "Snippets populares",
    tagline: "React, Express, NestJS, Spring, FastAPI, SQL e mais",
    description:
      "Atalhos de código que aparecem no autocompletar: componente React com hooks, rota Express, controller NestJS, consultas SQL, fetch com tratamento de erro, testes com Vitest e mais. Digite o prefixo (ex.: rfc, exroute, sqlsel) e escolha na lista.",
    category: "produtividade",
    version: "1.0.0",
    author: "Kompil",
    icon: "braces",
    permissions: ["editor.edit"],
    tags: ["snippets", "react", "express", "sql"],
  },
  {
    id: "kompil.markdown-preview",
    name: "Prévia de Markdown",
    tagline: "Veja o README renderizado sem sair do editor",
    description:
      "Comando para abrir a prévia do arquivo Markdown aberto (README, docs), atualizada enquanto você digita.",
    category: "produtividade",
    version: "1.0.0",
    author: "Kompil",
    icon: "eye",
    permissions: ["commands"],
    tags: ["markdown", "readme", "documentação"],
  },
  {
    id: "kompil.theme-light",
    name: "Tema claro",
    tagline: "A IDE em branco e preto, para ambientes bem iluminados",
    description:
      "Troca o tema escuro padrão pelo claro: fundo branco, texto preto e o código em tons de cinza. Como todo tema, é um por vez — ligar outro tema volta ao escuro.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "sun",
    permissions: ["ui.theme"],
    tags: ["tema", "claro", "branco", "light"],
    featured: true,
  },
  {
    id: "kompil.theme-hacker",
    name: "Tema Hacker",
    tagline: "Código verde e verde em negrito, como um terminal de filme",
    description:
      "Pinta só o código: tudo em tons de verde, com palavras-chave, funções, tipos e tags em verde negrito e comentários em verde apagado. A interface da IDE continua igual — preto e branco (ou o tema claro). Funciona no tema escuro e no claro.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "terminal",
    permissions: ["editor.theme"],
    tags: ["tema", "hacker", "verde", "matrix", "terminal"],
    featured: true,
  },
  {
    id: "kompil.theme-colorido",
    name: "Código colorido",
    tagline: "Sintaxe colorida mantendo a interface preto e branco",
    description:
      "Só o código ganha cor: palavras-chave, funções, tipos, textos e tags com cores distintas, e erros em vermelho, avisos em âmbar e sugestões da IA em violeta. A interface continua preto e branco.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["editor.theme"],
    tags: ["tema", "sintaxe", "cores"],
    featured: true,
  },
  {
    id: "kompil.theme-dracula",
    name: "Tema Dracula",
    tagline: "O clássico roxo, rosa e verde sobre fundo escuro",
    description:
      "Paleta inspirada no Dracula, um dos temas mais usados por devs. Os cursores dos colaboradores mantêm as cores de cada pessoa. Recolore a IDE inteira (painéis, menus e terminal), não só o código.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["editor.theme", "ui.theme"],
    tags: ["tema", "dracula", "escuro"],
  },
  {
    id: "kompil.theme-one-dark",
    name: "Tema One Dark",
    tagline: "A paleta equilibrada que ficou famosa no Atom",
    description:
      "Azul, roxo, verde e laranja suaves sobre cinza-azulado. Variante clara inclusa (One Light). Recolore a IDE inteira (painéis, menus e terminal), não só o código.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["editor.theme", "ui.theme"],
    tags: ["tema", "one dark", "atom"],
  },
  {
    id: "kompil.theme-github",
    name: "Tema GitHub",
    tagline: "As cores do código no GitHub, claro e escuro",
    description:
      "Sintaxe como você vê nos repositórios do GitHub, nas versões clara e escura. Recolore a IDE inteira (painéis, menus e terminal), não só o código.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["editor.theme", "ui.theme"],
    tags: ["tema", "github", "claro"],
  },
  {
    id: "kompil.theme-nord",
    name: "Tema Nord",
    tagline: "Tons árticos, frios e de baixo contraste",
    description:
      "Paleta polar com azuis e verdes gelados, confortável para longas sessões. Recolore a IDE inteira (painéis, menus e terminal), não só o código.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["editor.theme", "ui.theme"],
    tags: ["tema", "nord", "frio"],
  },
  {
    id: "kompil.theme-monokai",
    name: "Tema Monokai",
    tagline: "Rosa, verde-limão e amarelo de alto contraste",
    description:
      "A paleta vibrante do Sublime Text, para quem gosta de cor forte no código. Recolore a IDE inteira (painéis, menus e terminal), não só o código.",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["editor.theme", "ui.theme"],
    tags: ["tema", "monokai", "sublime"],
  },
  {
    id: "kompil.sidebar-color",
    name: "Cor da barra lateral",
    tagline: "Dê a sua cor à barra de ferramentas da IDE",
    description:
      "Pinta a barra lateral (as ferramentas à esquerda) com a cor que você escolher — azul-noite, roxo, verde, ciano, âmbar, vinho, rosa ou grafite — e, se quiser, também o painel do Explorador. A cor se mistura ao tema (claro ou escuro), então fica suave e os ícones continuam legíveis. Escolha em Barra lateral › Personalizar ou na paleta (Ctrl+Shift+P › Cor da barra lateral).",
    category: "aparencia",
    version: "1.0.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["ui.theme", "commands"],
    tags: ["cor", "barra lateral", "sidebar", "aparência", "personalizar"],
  },
  {
    id: "kompil.prettier",
    name: "Prettier",
    tagline: "Formata o arquivo com um atalho",
    description:
      "Formata JavaScript, TypeScript, CSS, HTML, JSON e Markdown com o Prettier, direto no navegador. A mudança entra como uma edição mínima no texto compartilhado: o cursor de quem está programando junto não pula.",
    category: "produtividade",
    version: "3.6.0",
    author: "Kompil",
    icon: "wand",
    permissions: ["editor.edit", "commands"],
    tags: ["formatação", "javascript", "typescript", "css"],
    featured: true,
  },
  {
    id: "kompil.theme-aurora",
    name: "Tema Aurora",
    tagline: "Sintaxe em cores frias sobre o preto",
    description:
      "Um tema escuro com sintaxe em azul, violeta e verde-água, pensado para longas sessões. Os cursores dos colaboradores continuam com as cores de cada pessoa. Recolore a IDE inteira (painéis, menus e terminal), não só o código.",
    category: "aparencia",
    version: "1.2.0",
    author: "Kompil",
    icon: "palette",
    permissions: ["editor.theme", "ui.theme"],
    tags: ["tema", "cores", "escuro"],
    featured: true,
  },
  {
    id: "kompil.theme-ember",
    name: "Tema Ember",
    tagline: "Sintaxe em tons quentes, alto contraste",
    description:
      "Tema com laranja, âmbar e rosa sobre fundo grafite. Contraste AA em todo o texto de código. Recolore a IDE inteira (painéis, menus e terminal), não só o código.",
    category: "aparencia",
    version: "1.0.3",
    author: "Kompil",
    icon: "flame",
    permissions: ["editor.theme", "ui.theme"],
    tags: ["tema", "cores", "quente"],
  },
  {
    id: "kompil.vim",
    name: "Modo Vim",
    tagline: "Edição modal com os atalhos do Vim",
    description:
      "Modos normal, inserção e visual, movimentos, registradores e comandos `:`. Funciona junto com a edição colaborativa: cada pessoa escolhe o próprio modo.",
    category: "editor",
    version: "0.19.0",
    author: "Kompil",
    icon: "keyboard",
    permissions: ["editor.keymap"],
    tags: ["vim", "atalhos", "modal"],
    featured: true,
  },
  {
    id: "kompil.indent-guides",
    name: "Guias de indentação",
    tagline: "Linhas verticais em cada nível de bloco",
    description: "Desenha guias discretas a cada nível de indentação para seguir blocos longos com o olho.",
    category: "editor",
    version: "1.1.0",
    author: "Kompil",
    icon: "indent",
    permissions: ["editor.decorate"],
    tags: ["indentação", "legibilidade"],
  },
  {
    id: "kompil.rainbow-brackets",
    name: "Parênteses arco-íris",
    tagline: "Cada nível de aninhamento com uma cor",
    description:
      "Colore (), [] e {} por profundidade. Ajuda a achar o fechamento certo em código muito aninhado.",
    category: "aparencia",
    version: "2.0.1",
    author: "Kompil",
    icon: "brackets",
    permissions: ["editor.decorate"],
    tags: ["parênteses", "cores", "legibilidade"],
  },
  {
    id: "kompil.todo-highlighter",
    name: "TODOs em destaque",
    tagline: "TODO, FIXME e HACK impossíveis de ignorar",
    description:
      "Destaca marcadores de pendência nos comentários e adiciona um comando para pular para o próximo.",
    category: "produtividade",
    version: "1.4.0",
    author: "Kompil",
    icon: "list-todo",
    permissions: ["editor.decorate", "commands"],
    tags: ["todo", "comentários"],
  },
  {
    id: "kompil.color-preview",
    name: "Prévia de cores",
    tagline: "Amostras ao lado de cada #hex e rgb()",
    description: "Mostra uma amostra da cor ao lado de valores hexadecimais e rgb/rgba em qualquer arquivo.",
    category: "editor",
    version: "1.0.0",
    author: "Kompil",
    icon: "swatch",
    permissions: ["editor.decorate"],
    tags: ["css", "cores", "design"],
  },
  {
    id: "kompil.whitespace",
    name: "Espaços visíveis",
    tagline: "Mostra espaços, tabs e espaço sobrando no fim",
    description: "Torna visíveis espaços e tabulações e destaca espaços em branco no fim das linhas.",
    category: "editor",
    version: "1.0.0",
    author: "Kompil",
    icon: "pilcrow",
    permissions: ["editor.decorate"],
    tags: ["espaços", "limpeza"],
  },
  {
    id: "kompil.zen-mode",
    name: "Modo zen",
    tagline: "Só você e o código",
    description:
      "Esconde painéis, terminal e barras com um atalho (Ctrl+K Z) e centraliza o editor. A presença dos colaboradores continua no topo.",
    category: "produtividade",
    version: "1.0.2",
    author: "Kompil",
    icon: "sparkles",
    permissions: ["ui.layout", "commands"],
    tags: ["foco", "layout"],
  },
  {
    id: "kompil.focus-timer",
    name: "Timer de foco",
    tagline: "Ciclos de 25 minutos na barra de status",
    description:
      "Um timer no estilo Pomodoro na barra de status: ciclos de foco e pausa, com aviso discreto ao terminar.",
    category: "produtividade",
    version: "1.3.0",
    author: "Kompil",
    icon: "timer",
    permissions: ["ui.statusbar", "commands"],
    tags: ["foco", "pomodoro", "tempo"],
  },
  {
    id: "kompil.doc-stats",
    name: "Estatísticas do arquivo",
    tagline: "Linhas, palavras e tempo de leitura",
    description: "Mostra na barra de status o total de linhas, palavras e caracteres do arquivo aberto.",
    category: "produtividade",
    version: "1.0.1",
    author: "Kompil",
    icon: "chart",
    permissions: ["ui.statusbar"],
    tags: ["estatísticas", "markdown"],
  },
  {
    id: "kompil.cursor-names",
    name: "Nomes nos cursores",
    tagline: "Sempre saiba quem está digitando onde",
    description:
      "Mantém o nome de cada colaborador visível ao lado do cursor, em vez de só ao passar o mouse.",
    category: "colaboracao",
    version: "1.0.0",
    author: "Kompil",
    icon: "users",
    permissions: ["editor.decorate"],
    tags: ["presença", "pareamento"],
  },
] as const satisfies readonly PluginManifest[];

export type PluginId = (typeof PLUGIN_CATALOG)[number]["id"];
export const PLUGIN_IDS = PLUGIN_CATALOG.map((p) => p.id) as PluginId[];

export function isPluginId(value: unknown): value is PluginId {
  return typeof value === "string" && (PLUGIN_IDS as string[]).includes(value);
}

export const UpdatePluginInput = z.object({ enabled: z.boolean() });

/** Plugin do catálogo com o estado da pessoa (resposta de GET /plugins). */
export interface PluginListing extends PluginManifest {
  installed: boolean;
  enabled: boolean;
  installs: number;
}
